<template>
  <div class="organogram-wrapper">
    <div class="organogram-actions" v-if="isAdmin">
      <div style="display:flex; align-items:center; gap: 20px; font-size: 13px;" class="mr-4">
        <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
          <input type="checkbox" v-model="showAdminManagers" /> Administrative Reporting Manager
        </label>
        <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
          <input type="checkbox" v-model="showTechManagers" /> Technical Reporting Manager
        </label>
      </div>
      <v-btn color="primary" small @click="addVacantNode" class="mr-2">Edit Organogram</v-btn>
      <v-btn color="success" small @click="saveLayout" class="mr-2">Save Layout</v-btn>
      <v-btn color="error" small @click="exportToPDF">Export PDF</v-btn>
    </div>
    <div class="organogram-actions" v-else>
      <div style="display:flex; align-items:center; gap: 20px; font-size: 13px;" class="mr-4">
        <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
          <input type="checkbox" v-model="showAdminManagers" /> Administrative Reporting Manager
        </label>
        <label style="cursor: pointer; display: flex; align-items: center; gap: 5px;">
          <input type="checkbox" v-model="showTechManagers" /> Technical Reporting Manager
        </label>
      </div>
      <v-btn color="error" small @click="exportToPDF">Export PDF</v-btn>
    </div>
    
    <div class="flow-container" ref="flowContainer" @mousemove="onMouseMove" @mouseup="onMouseUp">
      <!-- SVG layer for edges -->
      <svg class="edges-layer" style="width: 100%; height: 100%; overflow: visible;">
        <path
          v-for="edge in edges"
          :key="edge.id"
          :d="getEdgePath(edge)"
          stroke="#a5b1c2"
          stroke-width="1.5"
          fill="none"
        />
        <!-- Temporary edge being drawn -->
        <path
          v-if="drawingEdge"
          :d="drawingEdgePath"
          stroke="#2196F3"
          stroke-width="2"
          stroke-dasharray="5,5"
          fill="none"
        />
      </svg>

      <!-- HTML layer for nodes -->
      <div
        v-for="node in nodes"
        :key="node.id"
        :id="'node-' + node.id"
        :class="['custom-node', { 'vacant-node': node.data.isVacant, 'highlight-node': node.data.isHighlighted }]"
        :style="{
          left: node.position.x + 'px',
          top: node.position.y + 'px',
          backgroundColor: node.data.color,
          border: node.data.color === '#ffffff' ? '1px solid #1e7e34' : 'none',
          width: node.data.width ? node.data.width + 'px' : '140px',
          height: node.data.height ? node.data.height + 'px' : '70px'
        }"
        @mousedown="isAdmin ? onNodeMouseDown(node, $event) : null"
      >
        <!-- Connection dots for drawing edges -->
        <div v-if="isAdmin" class="conn-dot conn-dot-top" @mousedown.stop="startDrawEdge(node, 'top')"></div>
        <div v-if="isAdmin" class="conn-dot conn-dot-bottom" @mousedown.stop="startDrawEdge(node, 'bottom')"></div>
        <div v-if="isAdmin" class="conn-dot conn-dot-left" @mousedown.stop="startDrawEdge(node, 'left')"></div>
        <div v-if="isAdmin" class="conn-dot conn-dot-right" @mousedown.stop="startDrawEdge(node, 'right')"></div>
        
        <div 
          class="node-title" 
          :contenteditable="isAdmin" 
          @blur="updateNodeText(node, 'title', $event)"
          :style="{ color: getTextColor(node.data.color) }"
        >{{ node.data.title || node.data.employeeName }}</div>
        
        <div 
          class="node-subtitle" 
          :contenteditable="isAdmin" 
          @blur="updateNodeText(node, 'designation', $event)"
          :style="{ color: getTextColor(node.data.color) }"
        >{{ node.data.designation }}</div>
        
        <div 
          class="node-grade" 
          :contenteditable="isAdmin" 
          @blur="updateNodeText(node, 'grade', $event)"
          :style="{ color: getTextColor(node.data.color) }"
        >{{ node.data.grade || '' }}</div>

        <div 
          class="node-grade-range" 
          :contenteditable="isAdmin" 
          @blur="updateNodeText(node, 'gradeRange', $event)"
          :style="{ color: getTextColor(node.data.color) }"
          v-if="isAdmin || node.data.gradeRange"
        >{{ node.data.gradeRange || 'Grade Range' }}</div>
        
        <!-- Color Menu Toggle -->
        <div v-if="isAdmin && !isExporting" class="node-color-toggle" @click.stop="toggleColorMenu(node.id)" title="Change Color">
          🎨
        </div>
        
        <!-- Color Menu Dropdown -->
        <div v-if="isAdmin && activeColorMenu === node.id" class="node-color-dropdown" @mousedown.stop>
          <div class="color-option" @mousedown.stop="setColor(node, '#1e7e34')">
            <div class="color-dot" style="background-color: #1e7e34"></div>
            <span>Filled position</span>
          </div>
          <div class="color-option" @mousedown.stop="setColor(node, '#f57c00')">
            <div class="color-dot" style="background-color: #f57c00"></div>
            <span>New position</span>
          </div>
          <div class="color-option" @mousedown.stop="setColor(node, '#fbc02d')">
            <div class="color-dot" style="background-color: #fbc02d"></div>
            <span>Open-to-be-hired</span>
          </div>
          <div class="color-option" @mousedown.stop="setColor(node, '#ffffff')">
            <div class="color-dot" style="background-color: #ffffff; border: 1px solid #1e7e34;"></div>
            <span>Consultant / Muster Roll</span>
          </div>
        </div>
        
      </div>
    </div>
  </div>
</template>

<script>
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default {
  name: 'OrganogramFlow',
  props: {
    employees: { type: Array, default: () => [] },
    layout: { type: Object, default: () => ({ nodeStyles: [], customNodes: [], customEdges: [] }) },
    isAdmin: { type: Boolean, default: false },
    divisionName: { type: String, default: 'Division' },
    loggedInUserId: { type: String, default: null }
  },
  data() {
    return {
      nodes: [],
      edges: [],
      draggedNode: null,
      dragOffset: { x: 0, y: 0 },
      drawingEdge: null, // { sourceId, type: 'bottom', currentX, currentY }
      activeColorMenu: null,
      showAdminManagers: true,
      showTechManagers: false,
      isExporting: false,
    };
  },
  watch: {
    divisionId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchOrganogram();
        }
      }
    },
    showAdminManagers(newVal) {
      if (!newVal && !this.showTechManagers) {
        this.$nextTick(() => { this.showAdminManagers = true; });
        return;
      }
      this.buildElements();
    },
    showTechManagers(newVal) {
      if (!newVal && !this.showAdminManagers) {
        this.$nextTick(() => { this.showTechManagers = true; });
        return;
      }
      this.buildElements();
    },
    employees: { handler() { this.buildElements(); }, deep: true },
    layout: { handler() { this.buildElements(); }, deep: true }
  },
  mounted() {
    this.buildElements();
  },
  computed: {
    drawingEdgePath() {
      if (!this.drawingEdge) return '';
      const sourceNode = this.nodes.find(n => n.id === this.drawingEdge.sourceId);
      if (!sourceNode) return '';
      
      const startCoords = this.getAnchorCoords(sourceNode, this.drawingEdge.type);
      const startX = startCoords.x;
      const startY = startCoords.y;
      
      const endX = this.drawingEdge.currentX;
      const endY = this.drawingEdge.currentY;
      
      return `M ${startX} ${startY} L ${endX} ${endY}`;
    }
  },
  methods: {
    buildElements() {
      const nodes = [];
      const edges = [];
      const { nodeStyles = [], customNodes = [], customEdges = [] } = this.layout || {};

      const getActiveManagers = (emp) => {
        let active = [];
        if (this.showAdminManagers && emp.adminManagers) active.push(...emp.adminManagers);
        if (this.showTechManagers && emp.techManagers) active.push(...emp.techManagers);
        return [...new Set(active)];
      };

      // 1. Build a map to calculate tree hierarchy
      const treeLayout = {};
      const nodeMap = {};
      this.employees.forEach(emp => {
        nodeMap[emp._id] = { ...emp, children: [] };
      });

      const roots = [];
      this.employees.forEach(emp => {
        const activeManagers = getActiveManagers(emp);
        let hasActiveManagerInMap = false;
        
        if (activeManagers.length > 0) {
          for (let mid of activeManagers) {
            if (nodeMap[mid]) {
              hasActiveManagerInMap = true;
              break;
            }
          }
        }

        if (hasActiveManagerInMap) {
          const primaryActiveManager = activeManagers.find(mid => nodeMap[mid]);
          if (primaryActiveManager) {
            nodeMap[primaryActiveManager].children.push(nodeMap[emp._id]);
          }
        } else {
          roots.push(nodeMap[emp._id]);
        }
      });

      // Calculate subtree widths
      const calcWidth = (node) => {
        if (node.children.length === 0) {
          node.subtreeWidth = 160;
          return 160;
        }
        let width = 0;
        node.children.forEach(child => {
          width += calcWidth(child);
        });
        node.subtreeWidth = Math.max(width, 160);
        return node.subtreeWidth;
      };
      roots.forEach(root => calcWidth(root));

      // Assign coordinates
      const assignCoords = (node, x, y) => {
        treeLayout[node._id] = { x: x + node.subtreeWidth / 2 - 70, y };
        let currentX = x;
        node.children.forEach(child => {
          assignCoords(child, currentX, y + 100);
          currentX += child.subtreeWidth;
        });
      };

      let containerWidth = this.$refs.flowContainer ? this.$refs.flowContainer.clientWidth : 800;
      let totalWidth = roots.reduce((sum, root) => sum + root.subtreeWidth, 0) + Math.max(0, roots.length - 1) * 20;
      let startX = Math.max(20, (containerWidth - totalWidth) / 2);

      roots.forEach(root => {
        assignCoords(root, startX, 20);
        startX += root.subtreeWidth + 20; // Spacing between multiple roots
      });

      // 2. Create nodes and edges
      this.employees.forEach((emp) => {
        const style = nodeStyles.find(ns => ns.nodeId === emp._id) || {};
        let x = style.x;
        let y = style.y;

        // Auto layout if not positioned manually previously
        if (x === undefined || y === undefined) {
          x = treeLayout[emp._id] ? treeLayout[emp._id].x : 50;
          y = treeLayout[emp._id] ? treeLayout[emp._id].y : 50;
        }

        const isHighlighted = this.loggedInUserId && this.loggedInUserId === emp._id;

        nodes.push({
          id: emp._id,
          position: { x, y },
          data: {
            employeeName: style.title || emp.employeeName,
            designation: style.designation || emp.designation,
            grade: style.grade || emp.grade,
            gradeRange: style.gradeRange || '',
            title: style.title || emp.employeeName,
            isVacant: false,
            color: style.color || '#1e7e34',
            isHighlighted
          }
        });

        const activeManagers = getActiveManagers(emp);
        if (activeManagers.length > 0) {
          activeManagers.forEach(managerId => {
            if (nodeMap[managerId]) {
              edges.push({
                id: `e-${managerId}-${emp._id}`,
                source: managerId,
                target: emp._id
              });
            }
          });
        }
      });

      customNodes.forEach((cNode) => {
        nodes.push({
          id: cNode.nodeId,
          position: { x: cNode.x || 50, y: cNode.y || 50 },
          data: {
            title: cNode.title,
            designation: cNode.designation,
            grade: cNode.grade,
            gradeRange: cNode.gradeRange,
            isVacant: cNode.isVacant,
            color: cNode.color || '#fff3e0'
          }
        });
      });

      customEdges.forEach((cEdge, idx) => {
        edges.push({
          id: `ce-${cEdge.sourceNodeId}-${cEdge.targetNodeId}-${idx}`,
          source: cEdge.sourceNodeId,
          target: cEdge.targetNodeId,
          sourceAnchor: cEdge.sourceAnchor || 'bottom',
          targetAnchor: cEdge.targetAnchor || 'top'
        });
      });

      this.nodes = nodes;
      this.edges = edges;
      
      this.$nextTick(() => {
        this.$forceUpdate(); // Force re-render to calculate edge paths with actual DOM node heights
      });
    },
    getAnchorCoords(node, type) {
      const el = document.getElementById(`node-${node.id}`);
      const height = el ? el.offsetHeight : 100;
      const width = el ? el.offsetWidth : 150;
      let x = node.position.x;
      let y = node.position.y;
      
      if (type === 'top') {
        x += width / 2;
      } else if (type === 'bottom') {
        x += width / 2;
        y += height;
      } else if (type === 'left') {
        y += height / 2;
      } else if (type === 'right') {
        x += width;
        y += height / 2;
      } else {
        x += width / 2;
        y += height;
      }
      return { x, y };
    },
    getEdgePath(edge) {
      const source = this.nodes.find(n => n.id === edge.source);
      const target = this.nodes.find(n => n.id === edge.target);
      if (!source || !target) return '';

      let sType = edge.sourceAnchor;
      let tType = edge.targetAnchor;
      
      if (!sType || !tType) {
        const sx = source.position.x;
        const sy = source.position.y;
        const tx = target.position.x;
        const ty = target.position.y;
        
        if (Math.abs(sx - tx) > Math.abs(sy - ty) && Math.abs(sx - tx) > 100) {
          sType = sx < tx ? 'right' : 'left';
          tType = sx < tx ? 'left' : 'right';
        } else {
          sType = 'bottom';
          tType = 'top';
        }
      }
      
      const sCoords = this.getAnchorCoords(source, sType);
      const tCoords = this.getAnchorCoords(target, tType);
      
      let sx = sCoords.x, sy = sCoords.y;
      let tx = tCoords.x, ty = tCoords.y;
      
      if (sType === 'bottom' && tType === 'top') {
        let midY = sy + 15;
        if (ty < sy + 15) midY = (sy + ty) / 2;
        return `M ${sx} ${sy} L ${sx} ${midY} L ${tx} ${midY} L ${tx} ${ty}`;
      } else if (sType === 'top' && tType === 'bottom') {
        let midY = sy - 15;
        if (ty > sy - 15) midY = (sy + ty) / 2;
        return `M ${sx} ${sy} L ${sx} ${midY} L ${tx} ${midY} L ${tx} ${ty}`;
      } else if (sType === 'right' && tType === 'left') {
        let midX = sx + 5;
        if (tx < sx + 5) midX = (sx + tx) / 2;
        return `M ${sx} ${sy} L ${midX} ${sy} L ${midX} ${ty} L ${tx} ${ty}`;
      } else if (sType === 'left' && tType === 'right') {
        let midX = sx - 5;
        if (tx > sx - 5) midX = (sx + tx) / 2;
        return `M ${sx} ${sy} L ${midX} ${sy} L ${midX} ${ty} L ${tx} ${ty}`;
      } else if ((sType === 'bottom' || sType === 'top') && (tType === 'left' || tType === 'right')) {
        return `M ${sx} ${sy} L ${sx} ${ty} L ${tx} ${ty}`;
      } else if ((sType === 'left' || sType === 'right') && (tType === 'top' || tType === 'bottom')) {
        return `M ${sx} ${sy} L ${tx} ${sy} L ${tx} ${ty}`;
      } else {
        return `M ${sx} ${sy} L ${tx} ${ty}`;
      }
    },
    onNodeMouseDown(node, event) {
      this.draggedNode = node;
      const elRect = event.target.closest('.custom-node').getBoundingClientRect();
      
      // calculate offset from mouse to node top-left
      this.dragOffset.x = event.clientX - elRect.left;
      this.dragOffset.y = event.clientY - elRect.top;
    },
    startDrawEdge(node, type) {
      this.drawingEdge = { sourceId: node.id, type, currentX: 0, currentY: 0 };
    },
    updateNodeText(node, field, event) {
      if (field === 'title') {
        node.data.employeeName = event.target.innerText;
        node.data.title = event.target.innerText;
      } else if (field === 'designation') {
        node.data.designation = event.target.innerText;
      } else if (field === 'grade') {
        node.data.grade = event.target.innerText;
      } else if (field === 'gradeRange') {
        node.data.gradeRange = event.target.innerText;
      }
    },
    getTextColor(hexColor) {
      if (!hexColor) return '#000000';
      if (hexColor.startsWith('rgb')) return '#000000'; // simplified
      let r = 0, g = 0, b = 0;
      if (hexColor.length === 4) {
        r = parseInt(hexColor[1] + hexColor[1], 16);
        g = parseInt(hexColor[2] + hexColor[2], 16);
        b = parseInt(hexColor[3] + hexColor[3], 16);
      } else if (hexColor.length === 7) {
        r = parseInt(hexColor.substr(1, 2), 16);
        g = parseInt(hexColor.substr(3, 2), 16);
        b = parseInt(hexColor.substr(5, 2), 16);
      }
      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
      return (yiq >= 128) ? '#000000' : '#ffffff';
    },
    onMouseMove(event) {
      if (!this.isAdmin) return;
      const containerRect = this.$refs.flowContainer.getBoundingClientRect();
      const scrollLeft = this.$refs.flowContainer.scrollLeft;
      const scrollTop = this.$refs.flowContainer.scrollTop;
      
      if (this.draggedNode) {
        let newX = event.clientX - containerRect.left + scrollLeft - this.dragOffset.x;
        let newY = event.clientY - containerRect.top + scrollTop - this.dragOffset.y;
        
        // simple boundary check
        if (newX < 0) newX = 0;
        if (newY < 0) newY = 0;

        this.draggedNode.position.x = newX;
        this.draggedNode.position.y = newY;
      }
      
      if (this.drawingEdge) {
        this.drawingEdge.currentX = event.clientX - containerRect.left + scrollLeft;
        this.drawingEdge.currentY = event.clientY - containerRect.top + scrollTop;
      }
    },
    onMouseUp(event) {
      if (this.draggedNode) {
        this.draggedNode = null;
      }
      
      if (this.drawingEdge) {
        // Did we drop on another node?
        // simple hit test
        const containerRect = this.$refs.flowContainer.getBoundingClientRect();
        const scrollLeft = this.$refs.flowContainer.scrollLeft;
        const scrollTop = this.$refs.flowContainer.scrollTop;
        
        const dropX = event.clientX - containerRect.left + scrollLeft;
        const dropY = event.clientY - containerRect.top + scrollTop;
        
        const targetNode = this.nodes.find(n => {
           return dropX >= n.position.x && dropX <= n.position.x + 140 &&
                  dropY >= n.position.y && dropY <= n.position.y + 70;
        });

        if (targetNode && targetNode.id !== this.drawingEdge.sourceId) {
          const tx = targetNode.position.x;
          const ty = targetNode.position.y;
          const tw = 140;
          const th = 70;
          
          const distTop = Math.abs(dropY - ty);
          const distBottom = Math.abs(dropY - (ty + th));
          const distLeft = Math.abs(dropX - tx);
          const distRight = Math.abs(dropX - (tx + tw));
          
          let targetAnchor = 'top';
          let min = distTop;
          if (distBottom < min) { min = distBottom; targetAnchor = 'bottom'; }
          if (distLeft < min) { min = distLeft; targetAnchor = 'left'; }
          if (distRight < min) { min = distRight; targetAnchor = 'right'; }

          // Add custom edge
          this.edges.push({
            id: `ce-${this.drawingEdge.sourceId}-${targetNode.id}-${Date.now()}`,
            source: this.drawingEdge.sourceId,
            target: targetNode.id,
            sourceAnchor: this.drawingEdge.type,
            targetAnchor: targetAnchor
          });
        }
        
        this.drawingEdge = null;
      } else if (!this.draggedNode) {
        // click on container background
        this.activeColorMenu = null;
      }
    },
    addVacantNode() {
      const newNodeId = `vacant-${Date.now()}`;
      this.nodes.push({
        id: newNodeId,
        position: { x: 100, y: 100 },
        data: {
          title: 'Name',
          designation: 'Designation',
          grade: 'Grade',
          gradeRange: 'Grade Range',
          isVacant: true,
          color: '#ffffff'
        }
      });
    },
    saveLayout() {
      const nodeStyles = [];
      const customNodes = [];
      const customEdges = [];

      this.nodes.forEach(n => {
        if (n.id.startsWith('vacant-') || n.data.isVacant) {
          customNodes.push({
            nodeId: n.id,
            title: n.data.title,
            designation: n.data.designation,
            grade: n.data.grade,
            gradeRange: n.data.gradeRange,
            isVacant: true,
            color: n.data.color,
            x: n.position.x,
            y: n.position.y
          });
        } else {
          nodeStyles.push({
            nodeId: n.id,
            x: n.position.x,
            y: n.position.y,
            color: n.data.color,
            title: n.data.employeeName,
            designation: n.data.designation,
            grade: n.data.grade,
            gradeRange: n.data.gradeRange
          });
        }
      });

      this.edges.forEach(e => {
        // filter out auto edges
        const isAuto = this.employees.find(emp => emp._id === e.target && emp.reportingManagers && emp.reportingManagers.includes(e.source));
        if (!isAuto) {
          customEdges.push({ 
            sourceNodeId: e.source, 
            targetNodeId: e.target,
            sourceAnchor: e.sourceAnchor,
            targetAnchor: e.targetAnchor
          });
        }
      });

      this.$emit('save-layout', { nodeStyles, customNodes, customEdges });
    },
    toggleColorMenu(nodeId) {
      if (this.activeColorMenu === nodeId) {
        this.activeColorMenu = null;
      } else {
        this.activeColorMenu = nodeId;
      }
    },
    setColor(node, color) {
      node.data.color = color;
      this.activeColorMenu = null;
    },
    async exportToPDF() {
      this.isExporting = true;
      await this.$nextTick(); // Wait for UI to hide the color selector

      const el = this.$refs.flowContainer;
      if (!el) return;

      const originalOverflow = el.style.overflow;
      const originalScrollLeft = el.scrollLeft;
      const originalScrollTop = el.scrollTop;

      // Temporarily expand to capture full scrollable area
      el.style.overflow = 'visible';
      el.scrollLeft = 0;
      el.scrollTop = 0;

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#f4f5f7',
        width: el.scrollWidth,
        height: el.scrollHeight,
        windowWidth: el.scrollWidth,
        windowHeight: el.scrollHeight,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0
      });

      // Restore scroll and overflow
      el.style.overflow = originalOverflow;
      el.scrollLeft = originalScrollLeft;
      el.scrollTop = originalScrollTop;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      const maxImgWidth = pageWidth - 20;
      const maxImgHeight = pageHeight - 40; // leaving space for headers/footers

      let imgWidth = maxImgWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight > maxImgHeight) {
        imgHeight = maxImgHeight;
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }

      const xOffset = 10 + (maxImgWidth - imgWidth) / 2;

      pdf.setFontSize(16);
      pdf.text('Division Organogram', 14, 15);
      pdf.setFontSize(12);
      pdf.text(this.divisionName, 14, 22);

      pdf.addImage(imgData, 'PNG', xOffset, 30, imgWidth, imgHeight);

      const generatedOn = new Date().toLocaleString();
      pdf.setFontSize(10);
      pdf.text(`Generated On: ${generatedOn}`, 14, pageHeight - 10);

      pdf.save(`${this.divisionName.replace(/\s+/g, '_')}_Organogram.pdf`);
      
      this.isExporting = false;
    },
    async resetLayout() {
      if (confirm("Are you sure you want to reset the layout to default? This will clear all manually saved node positions.")) {
        this.$emit('save-layout', { nodeStyles: [], customNodes: [], customEdges: [] });
        this.layout = { nodeStyles: [], customNodes: [], customEdges: [] };
        setTimeout(() => {
          this.buildElements();
        }, 500);
      }
    }
  }
};
</script>

<style scoped>
.organogram-wrapper {
  display: flex;
  flex-direction: column;
  height: 800px;
  width: 100%;
}
.organogram-actions {
  padding: 10px;
  background: #f5f5f5;
  display: flex;
  justify-content: flex-end;
  border-bottom: 1px solid #ddd;
}
.flow-container {
  flex-grow: 1;
  background: #f4f5f7;
  position: relative;
  overflow: auto;
}
.edges-layer {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}
.custom-node {
  position: absolute;
  padding: 4px 6px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  text-align: center;
  font-family: Arial, sans-serif;
  cursor: grab;
  user-select: none;
  height: 70px;
  width: 140px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.custom-node:active {
  cursor: grabbing;
}
.vacant-node {
  border-color: #FF9800;
  border-style: dashed;
}
.highlight-node {
  border-width: 4px;
  border-color: #2E7D32;
}
.node-title {
  font-weight: bold;
  font-size: 10px;
  line-height: 1.2;
  margin-bottom: 2px;
  pointer-events: auto;
  outline: none;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}
.node-subtitle {
  font-size: 9px;
  line-height: 1.1;
  pointer-events: auto;
  outline: none;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}
.node-grade {
  font-size: 9px;
  line-height: 1.1;
  pointer-events: auto;
  outline: none;
  margin-top: 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
}
.node-grade-range {
  font-size: 9px;
  font-style: italic;
  pointer-events: auto;
  outline: none;
  margin-top: 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
}
.node-color-toggle {
  position: absolute;
  top: -10px;
  right: -10px;
  cursor: pointer;
  background: white;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  z-index: 20;
  font-size: 12px;
  border: 1px solid #ddd;
}
.node-color-dropdown {
  position: absolute;
  top: 15px;
  right: -60px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 30;
  width: 160px;
  display: flex;
  flex-direction: column;
  padding: 4px 0;
}
.color-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 10px;
  color: #333;
  text-align: left;
}
.color-option:hover {
  background: #f5f5f5;
}
.color-dot {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid transparent;
}
.color-dot:hover {
  transform: scale(1.2);
}
.conn-dot {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #2196F3;
  border-radius: 50%;
  cursor: crosshair;
}
.conn-dot-top {
  top: -5px;
  left: 50%;
  transform: translateX(-50%);
}
.conn-dot-bottom {
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
}
.conn-dot-left {
  left: -5px;
  top: 50%;
  transform: translateY(-50%);
}
.conn-dot-right {
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
}
</style>
